import React, { useState } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { richText } from '../../util/richText';
import { ValidationError, IconClose } from '../../components';
import { reorderOptionsWithSelectedFirst } from '../../util/search';

import css from './FieldSelectTree.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 16;
/**
 * Pick valid option configurations with format like:
 * [{ option, label, suboptions: [{ option, label }] }]
 *
 * @param {Array} options contain configs like { option, label, suboptions }
 * @param {Number} level specifies the nesting level
 * @returns an array of valid option configurations.
 */
const pickValidOptions = (options, level = 1) => {
  const isString = str => typeof str === 'string';
  const isValidOptions = opts => Array.isArray(opts);
  return options.reduce((picked, optionConfig) => {
    const { option, label, suboptions } = optionConfig;
    const isValid = isString(option) && isString(label);
    const suboptionsMaybe = isValidOptions(suboptions)
      ? { suboptions: pickValidOptions(suboptions, level + 1) }
      : {};
    const validOptionConfigMaybe = isValid ? [{ option, label, level, ...suboptionsMaybe }] : [];
    return [...picked, ...validOptionConfigMaybe];
  }, []);
};

const getSuboptions = optionConfig => optionConfig.suboptions;
const hasSuboptions = optionConfig => getSuboptions(optionConfig)?.length > 0;

/**
 * A component that represents a single option.
 *
 * @param {*} props include: config, level, handleChange, branchPath
 * @returns <li> wrapped elements.
 */
const Option = props => {
  const {
    config,
    level,
    handleChange,
    branchPath,
    selectedValues = [],
    showLeafClearButton,
    onClearSelection,
    clearSelectionAriaLabel,
    ...rest
  } = props;
  const { option, label, suboptions } = config;
  const foundFromBranchPath = branchPath.find(bc => bc.option === option);
  const isOptSelected = !!foundFromBranchPath;
  const selectedSuboptions = branchPath.filter(bc => bc.level > foundFromBranchPath?.level);
  const isSuboptionSelected = selectedSuboptions.length > 0;
  const isClickable = !isOptSelected || (isOptSelected && isSuboptionSelected);
  const cursorMaybe = isClickable ? { cursor: 'pointer' } : { cursor: 'default' };
  
  // Check if this option is in the selectedValues array (for reordering)
  const isInSelectedValues = selectedValues.includes(option);

  const optionLabel = richText(label || option, {
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  const buttonClasses = classNames({
    [css.optionBtn]: !isOptSelected || isSuboptionSelected,
    [css.optionBtnSelected]: isOptSelected && !isSuboptionSelected,
    [css.optionBtnSelectedLowest]: isOptSelected && !isSuboptionSelected,
    [css.optionBtnInSelectedValues]: isInSelectedValues && !isOptSelected,
  });

  const isLeafSelected = isOptSelected && !isSuboptionSelected;
  const showClearNextToSelected =
    showLeafClearButton && isLeafSelected && typeof onClearSelection === 'function';

  const optionButton = (
    <button
      className={buttonClasses}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        if (isClickable) {
          handleChange(option, level);
        }
      }}
    >
      {optionLabel}
    </button>
  );

  return (
    <li
      className={classNames(css.option, { [css.optionInSelectedValues]: isInSelectedValues })}
      style={{ paddingLeft: `${12}px`, ...cursorMaybe }}
    >
      {showClearNextToSelected ? (
        <div className={css.optionSelectedRow}>
          {optionButton}
          <button
            type="button"
            className={css.leafClearCircle}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onClearSelection();
            }}
            aria-label={clearSelectionAriaLabel}
          >
            <IconClose size="small" rootClassName={css.leafClearIcon} />
          </button>
        </div>
      ) : (
        optionButton
      )}

      {hasSuboptions(config) && isOptSelected ? (
        <SelectOptions
          options={suboptions}
          level={level + 1}
          handleChange={handleChange}
          branchPath={branchPath}
          selectedValues={selectedValues}
          showLeafClearButton={showLeafClearButton}
          onClearSelection={onClearSelection}
          clearSelectionAriaLabel={clearSelectionAriaLabel}
          {...rest}
        />
      ) : null}
    </li>
  );
};

/**
 * Presentational UI component wrapping <ul>.
 *
 * @param {*} props contains properties: hasOptions & children
 * @returns its children wrapped with <ul>
 */
const OptionList = props => {
  const { hasOptions, children } = props;
  return hasOptions ? <ul className={css.optionList}>{children}</ul> : null;
};

/**
 * Component, which allows user to select one of the options. Selected option might contain suboptions.
 *
 * @param {*} props must contain property options, which is an array of option configs.
 * @returns OptionList component.
 */
const SelectOptions = props => {
  const {
    options,
    selectedValues = [],
    showLeafClearButton,
    onClearSelection,
    clearSelectionAriaLabel,
    ...rest
  } = props;
  const intl = useIntl();
  const [showAll, setShowAll] = useState(false);

  // Reorder options to show selected values at the top
  const reorderedOptions = reorderOptionsWithSelectedFirst(options, selectedValues);

  const ITEMS_TO_SHOW = 5;
  const hasMoreItems = reorderedOptions?.length > ITEMS_TO_SHOW;
  const itemsToDisplay = showAll ? reorderedOptions : reorderedOptions?.slice(0, ITEMS_TO_SHOW);

  const handleShowMore = () => {
    setShowAll(true);
  };

  const handleShowLess = () => {
    setShowAll(false);
  };

  return (
    <OptionList hasOptions={itemsToDisplay?.length > 0}>
      {itemsToDisplay?.map(config => (
        <Option
          key={config.option}
          config={config}
          selectedValues={selectedValues}
          showLeafClearButton={showLeafClearButton}
          onClearSelection={onClearSelection}
          clearSelectionAriaLabel={clearSelectionAriaLabel}
          {...rest}
        />
      ))}
      {hasMoreItems && !showAll && (
        <li className={css.option} style={{ paddingLeft: `${12}px` }}>
          <button
            className={css.showMoreButton}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleShowMore();
            }}
          >
            {intl.formatMessage({ id: 'FieldSelectTree.showMore' }, { defaultMessage: 'Show more' })}
          </button>
        </li>
      )}
      {hasMoreItems && showAll && (
        <li className={css.option} style={{ paddingLeft: `${12}px` }}>
          <button
            className={css.showMoreButton}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleShowLess();
            }}
          >
            {intl.formatMessage({ id: 'FieldSelectTree.showLess' }, { defaultMessage: 'Show less' })}
          </button>
        </li>
      )}
    </OptionList>
  );
};

/**
 * Returns an array of selected nested option configurations.
 * It roughly looks like this:
 * [
 *   { option: 'women', label: 'Women', levelKey: 'categoryLevel1', suboptions: [etc.] },
 *   { option: 'jackets', label: 'Jackets', levelKey: 'categoryLevel2' },
 * ]
 *
 * @param {Array} primaryOptions highest level of options
 * @param {Object} currentSelections selected nested options: { categoryLevel1: 'women', categoryLevel2: 'jacket' }
 * @returns an array of selected nested option configurations.
 */
const getBranchPath = (primaryOptions, currentSelections) => {
  const currentSelectionsEntries = Object.entries(currentSelections).sort(([k, v]) => k);
  return currentSelectionsEntries.reduce((branchPath, entry) => {
    const [levelKey, selectedOption] = entry;

    const currentOptions =
      branchPath.length > 0 ? branchPath[branchPath.length - 1].suboptions : primaryOptions;
    const foundOption = currentOptions.find(o => o.option === selectedOption);
    const foundOptionWithLevelKeyMaybe = foundOption ? [{ ...foundOption, levelKey }] : [];

    return [...branchPath, ...foundOptionWithLevelKeyMaybe];
  }, []);
};

/**
 * Handle value changes that happen when user clicks different options.
 *
 * @param {Array} primaryOptions highest level of options
 * @param {Object} currentSelections selected nested options: { categoryLevel1: 'women', categoryLevel2: 'jacket' }
 * @param {String} namePrefix like "categoryLevel"
 * @param {Function} onChange Final Form's onChange function.
 */
const handleChangeFn = (primaryOptions, currentSelections, namePrefix, onChange) => (
  currentOption,
  level
) => {
  const branchPath = getBranchPath(primaryOptions, currentSelections);

  const updatedCurrentBranchPath = branchPath.reduce((picked, selectedOptionByLevel) => {
    const { level: branchPathLevel, option: optionByLevel } = selectedOptionByLevel;
    const levelKey = `${namePrefix}${branchPathLevel}`;

    return branchPathLevel < level
      ? { ...picked, [levelKey]: optionByLevel }
      : branchPathLevel === level
      ? { ...picked, [levelKey]: currentOption }
      : picked;
  }, {});

  const levelKeyToUpdate = `${namePrefix}${level}`;
  const isUpdateANewBranchPath = updatedCurrentBranchPath[levelKeyToUpdate] == null;
  const updatedValue = isUpdateANewBranchPath
    ? { ...updatedCurrentBranchPath, [levelKeyToUpdate]: currentOption }
    : updatedCurrentBranchPath;

  onChange(updatedValue);
};

/**
 * @typedef {Object} SelectTreeOptionConfig
 * @property {string} option
 * @property {string} label
 * @property {Array<SelectTreeOptionConfig>} suboptions
 */

/**
 * Create Final Form field that represents "tree select" component,
 * where user can select nested options like categories.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.name Name of the input in Final Form
 * @param {ReactNode} props.label
 * @param {Array<SelectTreeOptionConfig>} props.options
 * @param {Array<string>} [props.selectedValues] - Array of selected option values to show at the top
 * @param {boolean} [props.showLeafClearButton] - Show circled clear next to the active leaf (optionBtnSelected)
 * @returns {JSX.Element} Final Form Field containing nested "select" input
 */
const FieldSelectTree = props => {
  const {
    className,
    rootClassName,
    label,
    name,
    options,
    selectedValues = [],
    showLeafClearButton = false,
    ...rest
  } = props;
  const intl = useIntl();
  const namePrefix = name;
  const level = 1;
  const validOptions = pickValidOptions(options);

  const classes = classNames(rootClassName || css.root, className);
  const clearSelectionAriaLabel = intl.formatMessage({ id: 'FilterPlain.clear' });

  return (
    <Field name={name} {...rest}>
      {fieldProps => {
        const { value, onChange } = fieldProps?.input || {};
        const fieldValue = value ? value : {};
        const branchPath = getBranchPath(validOptions, fieldValue);
        const meta = fieldProps?.meta;
        const onClearSelection = () => onChange({});

        return (
          <div className={classes}>
            {label ? <label>{label}</label> : null}
            <SelectOptions
              options={validOptions}
              level={level}
              handleChange={handleChangeFn(validOptions, fieldValue, namePrefix, onChange)}
              branchPath={branchPath}
              selectedValues={selectedValues}
              showLeafClearButton={showLeafClearButton}
              onClearSelection={onClearSelection}
              clearSelectionAriaLabel={clearSelectionAriaLabel}
            />

            <ValidationError fieldMeta={meta} />
          </div>
        );
      }}
    </Field>
  );
};

export default FieldSelectTree;
